/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { DragSource as dragSource, DropTarget as dropTarget } from 'react-dnd';
import { lodash } from '@ba-ui-toolkit/utils';
import scrollableDnD from './scrollableDnD';

const { omit } = lodash;

let lastHoveredYPercent;
let lastHoveredXPercent;
const idFn = x => x;

export const ITEM_TYPE = 'ITEM_TYPE';

export default function(
	Comp,
	{ dragItemType, dropItemType, simpleHoverStates = true, scrollable = false }
) {
	const getHoveredY = function getHoveredY() {
		if (simpleHoverStates) {
			return lastHoveredYPercent >= 50;
		}

		return lastHoveredYPercent;
	};

	const getHoveredX = function getHoveredX() {
		if (simpleHoverStates) {
			return lastHoveredXPercent >= 50;
		}

		return lastHoveredXPercent;
	};

	const dropTargetMethods = {
		canDrop(props, monitor) {
			if (props.canDrop) {
				return props.canDrop(
					(props.getDropProps || idFn)(props, getHoveredY(), getHoveredX()),
					monitor.getItem(),
					monitor
				);
			}

			return false;
		},

		drop(props, monitor, component) {
			// eslint-disable-line no-unused-vars
			if (props.onDrop && !monitor.didDrop()) {
				props.onDrop(
					(props.getDropProps || idFn)(props, getHoveredY(), getHoveredX()),
					monitor.getItem(),
					monitor
				);
			}
		},

		hover(props, monitor, component) {
			const hoverBoundingRect = (
				props.customDnDNode || ReactDOM.findDOMNode(component)
			).getBoundingClientRect();
			const clientOffset = monitor.getClientOffset();
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			const hoverClientX = clientOffset.x - hoverBoundingRect.left;

			lastHoveredYPercent =
				(hoverClientY / (hoverBoundingRect.bottom - hoverBoundingRect.top)) * 100;
			lastHoveredXPercent =
				(hoverClientX / (hoverBoundingRect.right - hoverBoundingRect.left)) * 100;

			if (props.onHover) {
				props.onHover(
					(props.getDropProps || idFn)(props, getHoveredY(), getHoveredX()),
					monitor.getItem(),
					monitor
				);
			}

			if (simpleHoverStates) {
				// forceUpdate in order to pass down updated hover states to child component
				component.forceUpdate();
			}
		},
	};

	const dragSourceMethods = {
		beginDrag(props) {
			const { onBeginDrag } = props;

			return (onBeginDrag || idFn)(props);
		},

		canDrag(props) {
			const { canDragNode } = props;

			return !canDragNode || canDragNode(props);
		},

		endDrag(props) {
			props.onEndDrag && props.onEndDrag(props);
		},
	};

	const addDropTarget = () =>
		dropTarget(dropItemType, dropTargetMethods, (connect, monitor) => ({
			connectDropTarget: connect.dropTarget(),
			isActive: () => monitor.isOver() && monitor.canDrop(),
		}));

	const addDragSource = () =>
		dragSource(dragItemType, dragSourceMethods, connect => ({
			connectDragSource: connect.dragSource(),
		}));

	let WrappedComponent = Comp;

	if (scrollable) {
		WrappedComponent = scrollableDnD(Comp);
	}

	class Item extends Component {
		static propTypes = {
			customDnDNode: PropTypes.object,
			dragging: PropTypes.bool,
			itemRenderer: PropTypes.func, // NOTE: isRequired causing warning 'undefined' in console. Removed isRequired for now - can be added back if any side effects or new insights.
			connectDropTarget: PropTypes.func.isRequired,
			connectDragSource: PropTypes.func.isRequired,
			dragLayerRenderer: PropTypes.func, // NOTE: isRequired causing warning 'undefined' in console. Removed isRequired for now - can be added back if any side effects or new insights.
			isActive: PropTypes.func.isRequired,
			getDropProps: PropTypes.func.isRequired,
			onBeginDrag: PropTypes.func.isRequired,
			itemDnDForwardRef: PropTypes.any,
		};

		_resovleRef = ref => {
			const node = ReactDOM.findDOMNode(ref);
			if (node && !this.props.customDnDNode) {
				this._connectToDnD(node);
			}
			const { itemDnDForwardRef } = this.props;

			itemDnDForwardRef && itemDnDForwardRef(ref);
		};

		_connectToDnD = node => {
			const { connectDragSource, connectDropTarget } = this.props;
			connectDropTarget && connectDropTarget(node);
			connectDragSource && connectDragSource(node);
		};

		componentDidMount() {
			const { customDnDNode } = this.props;
			if (customDnDNode) {
				this._connectToDnD(customDnDNode);
			}
		}

		componentWillReceiveProps(nextProps) {
			if (nextProps.customDnDNode && nextProps.customDnDNode !== this.props.customDnDNode) {
				this._connectToDnD(nextProps.customDnDNode);
			}
		}

		render() {
			const { isActive, dragLayerRenderer, ...other } = this.props;
			const passThroughProps = {
				...omit(
					other,
					'canDrop',
					'canDragNode',
					'onBeginDrag',
					'onEndDrag',
					'connectDropTarget',
					'connectDragSource',
					'getDropProps',
					'onHover',
					'customDnDNode',
					'itemDnDForwardRef'
				),
			};

			const active = isActive && isActive();
			let hoverProps = { hoveredActive: active };

			if (active && simpleHoverStates) {
				const hoveredBelow = getHoveredY();
				const hoveredRight = getHoveredX();

				hoverProps = {
					...hoverProps,
					hoveredRight,
					hoveredBelow,
					hoveredAbove: !hoveredBelow,
					hoveredLeft: !hoveredRight,
				};
			}

			return (
				<WrappedComponent {...hoverProps} {...passThroughProps} ref={this._resovleRef} />
			);
		}
	}

	let ItemDnD = Item;

	if (dragItemType) {
		ItemDnD = addDragSource()(ItemDnD);
	}

	if (dropItemType) {
		ItemDnD = addDropTarget()(ItemDnD);
	}

	if (ItemDnD === Item) {
		throw new Error("itemDnD: 'dragItemType' and/or 'dropItemType' must be provided");
	}

	return React.forwardRef((props, ref) => <ItemDnD {...props} itemDnDForwardRef={ref} />);
}
